// Re-running the weak races of a meeting from the Auto review tab: one button per model, each re-running that
// model on every C or D race, back into the slot it already holds in that race (slots get swapped per race, so
// a model is not always in the same slot). Pure, so it unit-tests without React.

import { AnalysisModel, buildModelWebhookUrl, sortedActiveModels } from './analysis-models';

/** The grades worth re-running: C (liveable, slot 1 outvoted) and D (no majority). */
export const RERUN_GRADES: readonly string[] = ['C', 'D'];

interface DecisionLike {
  race_number: number | null;
  grade: string;
  candidates: { slot: number; model: string | null }[];
}

export interface RerunRace {
  raceNumber: number;
  slot: number;
}

export interface ModelRerun {
  /** the model as the auto review names it: "<ai_model> <ai_version>", e.g. "Claude Sonnet 5" */
  model: string;
  /** the registry entry that runs it; null when no active model matches (the button cannot run) */
  registry: AnalysisModel | null;
  races: RerunRace[];
}

/** "<ai_model> <ai_version>", trimmed - how the auto review (changeset 0063) names a candidate's model. */
export function modelName(m: Pick<AnalysisModel, 'ai_model' | 'ai_version'>): string {
  return `${m.ai_model ?? ''} ${m.ai_version ?? ''}`.trim();
}

/**
 * One entry per model that ran on a C or D race, with the races to re-run and the slot to write each into.
 * Models are in slot order of first appearance; races in race order. A registry name shared by two entries
 * (e.g. Sonnet 5 through the API and through Homelabs) resolves to the first in the run picker's order.
 */
export function planReruns(decisions: readonly DecisionLike[], models: readonly AnalysisModel[]): ModelRerun[] {
  const active = sortedActiveModels([...models]);
  const byModel = new Map<string, RerunRace[]>();
  const weak = decisions
    .filter((d) => RERUN_GRADES.includes(d.grade) && d.race_number != null)
    .sort((a, b) => a.race_number! - b.race_number!);
  const ordered = weak.flatMap((d) =>
    [...d.candidates].sort((a, b) => a.slot - b.slot).map((c) => ({ raceNumber: d.race_number!, ...c })),
  );
  for (const c of ordered) {
    if (!c.model) continue;
    // a model in two slots of one race (Claude in 1 and 4, say) re-runs both
    byModel.set(c.model, [...(byModel.get(c.model) ?? []), { raceNumber: c.raceNumber, slot: c.slot }]);
  }
  return [...byModel.entries()].map(([model, races]) => ({
    model,
    registry: active.find((m) => modelName(m) === model) ?? null,
    races: races.sort((a, b) => a.raceNumber - b.raceNumber || a.slot - b.slot),
  }));
}

/** The webhook URLs that re-run one model's races, one per race (a robot run takes one race or the whole meeting). */
export function rerunUrls(
  base: string,
  rerun: ModelRerun,
  meeting: { trackId: string; meetingDate: string },
): string[] {
  if (!rerun.registry) return [];
  const path = rerun.registry.webhook_path;
  return rerun.races.map((r) =>
    buildModelWebhookUrl(base, path, {
      track_id: meeting.trackId,
      meeting_date: meeting.meetingDate,
      race_number: r.raceNumber,
      analysis_type_id: r.slot,
    }),
  );
}
